<template>
  <v-dialog v-model="value" max-width="700px" persistent>
    <v-card>
      <v-card-title>
        <span class="headline font-weight-thin" v-if="!dropdownValue.id">Create New Dropdown Value</span>
        <span class="headline font-weight-thin" v-else> Edit: {{ dropdownValueOriginal.label }}</span>
      </v-card-title>
      <v-card-text class="mt-2">
        <v-form v-model="valid" ref="form">
          <v-text-field
            :rules="labelRules"
            class="mb-2 mt-3"
            clearable
            data-cy="dropdownValueLabel"
            label="Dropdown Value Label"
            outlined
            ref="labelInput"
            required
            v-model="dropdownValue.label"
          ></v-text-field>
          <v-textarea
            auto-grow
            class="mb-2 mt-3"
            clearable
            counter="250"
            data-cy="dropdownValueDescription"
            dense
            label="Dropdown Value Description"
            maxlength="250"
            outlined
            v-model="dropdownValue.description"
          ></v-textarea>
          <div v-if="grouperList">
            <v-autocomplete
              @change="onGrouperChange"
              :disabled="dropdownValueIsInUse"
              :item-text="grouperDisplayText"
              :items="grouperList"
              :label="`Grouper: ${groupDropdown.label}`"
              :rules="grouperRules"
              data-cy="groupDropdownValue"
              item-value="id"
              outlined
              ref="grouperInput"
              required
              v-model="dropdownValue.groupDropdownValueId"
            >
            </v-autocomplete>
          </div>
          <v-alert type="info" color="primary" dense outlined v-if="grouperList && !releaseNoteListIsEmpty">
            Dropdown Value is used by {{ dropdownValue.releaseNoteList.length }} Release Note(s). It cannot be moved to
            a different {{ groupDropdown.label }}.
          </v-alert>

          <div v-if="dropdownValue.id && dropdownValueIsInUse">
            <v-checkbox
              :false-value="0"
              :true-value="1"
              data-cy="checkbox-deprecated"
              data-test="checkbox-deprecated"
              label="Deprecated"
              v-model="dropdownValue.isDeprecated"
            ></v-checkbox>
          </div>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn @click="close" data-cy="cancelButton" outlined small>Cancel</v-btn>
            <v-btn @click="submit" :disabled="disableSubmit" color="primary" data-cy="saveButton" outlined small
              >Save</v-btn
            >
          </v-card-actions>
        </v-form>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { isEmpty, isEqual } from "lodash";
import { mapActions, mapGetters, mapState } from "vuex";
export default {
  computed: {
    disableSubmit() {
      return !this.valid || this.dropdownValueIsUnchanged;
    },
    dropdownValueIsUnchanged() {
      return isEqual(this.dropdownValueOriginal, this.dropdownValue);
    },
    labelValidationMessage() {
      if (this.grouperList) {
        return "This pairing of label and grouper is in use.";
      }
      return "Label must be unique.";
    },
    releaseNoteListIsEmpty() {
      return isEmpty(this.dropdownValue.releaseNoteList);
    },
    ...mapGetters({ getDropdownById: "admin/dropdown/getById" }),
    ...mapState({
      dropdownValue: state => state.admin.dropdownValue.record,
      dropdown: state => state.admin.dropdown.record,
    }),
  },
  data() {
    return {
      dropdownValueOriginal: { label: null },
      grouperRules: [
        v => !!v || "Group Dropdown is required.",
        () => this.validateUniqueConstraint() || `${this.labelValidationMessage}`,
      ],
      labelRules: [
        name => !!name || "Label is required",
        () => this.validateUniqueConstraint() || `${this.labelValidationMessage}`,
      ],
      valid: false,
    };
  },
  methods: {
    checkForLabelConflict() {
      const conflict = this.dropdown.valueList.find(dropdown => {
        const labelMatch =
          `${dropdown.label}`.trim().toUpperCase() === `${this.dropdownValue.label}`.trim().toUpperCase();
        const grouperMatch = dropdown.groupDropdownValueId === this.dropdownValue.groupDropdownValueId;
        return labelMatch && grouperMatch && !(dropdown.id === this.dropdownValue.id);
      });
      return conflict ? true : false;
    },
    close() {
      this.$emit("input", false);
      this.$refs.form.resetValidation();
    },
    validateUniqueConstraint() {
      if (!this.dropdownValue.label || !this.$refs.form) return true;
      if (this.dropdownValueIsUnchanged) {
        return true;
      }
      return this.checkForLabelConflict() ? false : true;
    },
    grouperDisplayText(item) {
      if (item) {
        const deprecatedText = item.isDeprecated ? "(DEPRECATED)" : "";
        return `${item.label} ${deprecatedText}`;
      }
    },
    onGrouperChange() {
      this.$nextTick().then(() => {
        this.$refs.form.validate();
      });
    },
    async submit() {
      const dropdownValueData = Object.assign({}, this.dropdownValue);
      delete dropdownValueData.releaseNoteList;
      dropdownValueData.label = dropdownValueData.label.trim();

      if (typeof dropdownValueData.description === "string" && dropdownValueData.description.length > 0) {
        dropdownValueData.description = dropdownValueData.description.trim();
      } else {
        dropdownValueData.description = null;
      }

      if (this.dropdownValue.id) {
        await this.submitUpdatedRecord(dropdownValueData);
      } else {
        await this.submitNewRecord(dropdownValueData);
      }

      try {
        // fetching the dropdown record again to get the updated valueList for subsequent validation checks
        await this.fetchDropdown(this.$route.params.id);
      } catch (e) {
        console.error(e);
        this.$notifyError("Error refreshing dropdown data.");
      }
    },
    resetCreateForm() {
      this.$refs.form.reset();
      this.$refs.form.resetValidation();
      this.dropdownValue.isDeprecated = 0;
    },
    async submitNewRecord(val) {
      try {
        await this.createDropdownValue(val);
        this.$notifySuccess({ message: "Successfully created a new dropdown value." });
        this.close();
      } catch (e) {
        console.error(e);
        return this.$notifyError({ message: "There was an error creating a new dropdown value." });
      }
    },
    async submitUpdatedRecord(val) {
      try {
        await this.updateDropdownValue(val);
        this.$notifySuccess({ message: "Successfully updated dropdown value." });
        this.close();
      } catch (e) {
        console.error(e);
        return this.$notifyError({ message: "There was an error updating the dropdown value." });
      }
    },
    ...mapActions({
      createDropdownValue: "admin/dropdownValue/create",
      fetchDropdown: "admin/dropdown/fetchRecord",
      fetchDropdownList: "admin/dropdown/list",
      updateDropdownValue: "admin/dropdownValue/update",
    }),
  },
  props: {
    dropdownValueIsInUse: { required: true, type: Boolean },
    groupDropdown: { type: Object },
    grouperList: { type: Array },
    value: { required: true, type: Boolean },
  },
  watch: {
    value(dialogOpen) {
      if (!dialogOpen) {
        return;
      }

      if (!this.dropdownValue.id && this.$refs.form) {
        this.resetCreateForm();
      }

      if (this.dropdownValue.id) {
        this.dropdownValueOriginal = Object.assign({}, this.dropdownValue);
      }
    },
    "dropdownValue.label": {
      handler(val) {
        if (this.grouperList) {
          this.$nextTick(() => {
            this.$refs.grouperInput.validate();
          });
        }
      },
    },
  },
};
</script>
